import _typeof from "@babel/runtime/helpers/typeof";
import Handlebars from '@sparta.global.vendors/handlebars';
import { link as link$1 } from '@sparta.global.helpers/link-helper/dist/es5.js';

/**
 * If current and next object values are the same, remove next from object.
 * Allows for mobile-first DOM markup
 * Example `medium-collapse large-collapse` should export `medium-collapse`.
 * @param {object} object - Column or breakpoint sizing data.
 * @param {array} objectKeys - Optional keys to provide to help reduce JavaScript redundant calls.
 * @param {array} objectValues - Optional values to provide to help reduce JavaScript redundant calls.
*/
function deleteObjItem(object, objectKeys, objectValues) {
  var objKeys = objectKeys || Object.keys(object);
  var objValues = objectValues || Object.values(object);

  // Loop through values and check next instance if it matches current
  var itemsToRemove = [];
  objKeys.forEach(function (key, index) {
    var currentItem = objValues[index];
    var nextItem = objValues[index + 1];
    if (currentItem === nextItem) {
      itemsToRemove.push(objKeys[index + 1]);
    }
  });

  // Remove unwanted items from object
  itemsToRemove.forEach(function (item) {
    delete object[item];
  });
  return object;
}

/**
   * Link helper.
   * @param {object} linkData - Data for the `a` tag.
   * @returns {object} The generated markup wrapped to indicate it should never be escaped.
   */
function link(linkData) {
  return link$1(linkData);
}

/**
 * Layer Content helper.
 * @param {object} data - Data to be included in the `data-options` attribute.
 * @param {object} options - Layer content to be wrapped.
 * @returns {object} The generated markup wrapped to indicate it should never be escaped.
 */
function layerContent(data, options) {
  var escapedOptions = Handlebars.Utils.escapeExpression(JSON.stringify(data.options));

  // set title tag type
  var tagType = data.options.type === 'modal' ? 'h1' : 'h3';

  // set ui layer title
  var showTitle = '';
  if (data.title) {
    showTitle = "<".concat(tagType, " id=\"").concat(data.id, "_title\" class=\"spa-ui-layer-title\" data-font=\"cnx-bold\">").concat(data.title, "</").concat(tagType, ">");
  }

  // set block content
  var content = '';
  if (options && options.isBlockHelperOptions) {
    var blockHelperTemplate = Handlebars.compile("".concat(options.helperBlockContent));
    content = blockHelperTemplate(data);
  }
  if (options && Object.prototype.hasOwnProperty.call(options, 'fn')) {
    content = options.fn(this);
  }

  // set ui layer footer content
  var footerContent = '';
  if (data.footer) {
    for (var index in data.footer) {
      footerContent += data.footer[index];
    }
    footerContent = "<div id=\"".concat(data.id, "_footer\" class=\"spa-footer\">").concat(footerContent, "</div>");
  }

  // generate ui layer core markup
  var output = "<div id=\"".concat(data.id, "\" class=\"hide\" data-options=\"").concat(escapedOptions, "\">\n      ").concat(showTitle, "\n      <div class=\"content\">").concat(content, "</div>\n      ").concat(footerContent, "\n  </div>");
  return new Handlebars.SafeString(output);
}

/**
 * Abbreviation helper.
 * @param {object} a - Data for the `abbr` tag.
 * @returns {string} The generated markup.
 */
function abbr(a) {
  var abbr = a;
  if (!abbr) {
    return '';
  }
  var value = abbr.value;
  delete abbr.value;
  var attr = '';
  var attrTemplate = function attrTemplate(n, v) {
    return "".concat(n, "=\"").concat(v, "\"");
  };
  var abbrTemplate = function abbrTemplate(attributes, text) {
    return "<abbr ".concat(attributes, ">").concat(text, "</abbr>");
  };
  Object.keys(abbr).forEach(function (key) {
    attr += attrTemplate(key, abbr[key]);
  });
  return abbrTemplate(attr, value);
}

/**
 * ADA block helper. Adds `ada-hidden` class.
 * @param {object} options - Content for the hidden `span`.
 * @returns {object} The generated markup wrapped to indicate it should never be escaped.
 */
function ada(options) {
  return new Handlebars.SafeString("<span class=\"ada-hidden\" data-version=\"2.0.0\">".concat(options.fn(this), "</span>"));
}

/**
 * Debug helper. Console logs out debug message.
 * @param {string} logName - Log name label.
 * @returns {object} The generated markup wrapped to indicate it should never be escaped.
 */
function debug(logName) {
  var label = typeof logName !== 'undefined' ? "Sparta Debug: ".concat(logName) : '';
  var output = "<script>\n      console.group('".concat(label, "');\n      console.group(").concat(JSON.stringify(this), ");\n      console.groupEnd();\n      console.groupEnd();\n    </script>");
  return new Handlebars.SafeString(output);
}

/**
 * Debug print helper. Prints out debug message on the page.
 * @param {string} logName - Log name label.
 * @returns {object} The generated markup wrapped to indicate it should never be escaped.
 */
function debug_print(logName) {
  var label = typeof logName !== 'undefined' ? "Sparta Debug: ".concat(logName) : '';
  var debugStyles = {};
  debugStyles.wrapper = 'position:relative; border:5px solid #333; padding:18px 10px 10px; margin: 5px 0;';
  debugStyles.header = 'position: absolute; top: 0; left: 0; width: 100%; height: 18px; font-size: 11px; background-color: #333; color: #fff; padding: 0; margin: 0;';
  var output = "<div class=\"spa-debug\" data-debug=\"".concat(label, "\" style=\"").concat(debugStyles.wrapper, "\">\n      <pre><h3 style=\"").concat(debugStyles.header, "\">").concat(label, "</h3>\n      ").concat(JSON.stringify(this, 0, 2), "\n      </pre>\n    </div>");
  return new Handlebars.SafeString(output);
}

/**
 * @param {object} attributes - holds the values to get added to the element on compile.
 * @returns {string} output - this is the string that gets built from each of the keys in the JCR.
 */
function elementAttributes(attributes) {
  if (!attributes) return;
  var output = [];
  var attrs = Object.entries(attributes);
  attrs.forEach(function (el) {
    var elementAttribute = el[0];
    var elementAttributeData = el[1];
    // Check to see if attribute is an Object data type.
    if (_typeof(elementAttributeData) === 'object') {
      // If the value is an Object, check to see if the value is an Array.
      if (Array.isArray(elementAttributeData)) {
        // If the value is an Array, assign all elements of the array to the attribute.
        // This is intended to be for the style attribute for inline styles, You will need each index to end with a semicolon (';'), and should be separated by a space(' ');
        var tempArray = [];
        elementAttributeData.forEach(function (el) {
          var index = el.trim();
          if (index.substring(index.length - 1) !== ';') {
            index += ';';
          }
          tempArray.push(index);
        });
        output.push("".concat(elementAttribute.toLowerCase(), "=\"").concat(tempArray.join(' '), "\""));
      } else {
        // If the value is an Object, iterate over each key prefixing with the object name assigning the value of the key.
        var prefix = elementAttribute.toLowerCase();
        Object.keys(elementAttributeData).forEach(function (el) {
          return output.push("".concat(prefix, "-").concat(el, "=\"").concat(attributes[elementAttribute][el], "\""));
        });
      }
      // If the value is not an object, check for a boolean. If the boolean is true then add the attribute only to the element.
    } else if (typeof elementAttributeData === 'boolean') {
      if (elementAttributeData === true) {
        output.push("".concat(elementAttribute));
      }
      // Failing the other checks, just assign the attribute the key with the value.
    } else {
      output.push("".concat(elementAttribute, "=\"").concat(elementAttributeData, "\""));
    }
  });
  // Creates a Handlebars Safe String to compile in to the HTML.
  return new Handlebars.SafeString(" ".concat(output.join(' ')));
}

/**
 * Sprite Rules helper.
 * @param {object} o - Data for the sprite's CSS.
 * @returns {string} The generated CSS.
 */
function spriteRules(o) {
  var output = '';
  var writeRule = function writeRule(exp) {
    var css = '';
    switch (exp.experience) {
      case 'hires':
        css += 'html.mq-hires ';
        break;
      case 'fallback':
        css += 'html.lt-ie9 ';
        break;
    }
    if (exp.context !== 'default') {
      css += ".".concat(exp.context, " ");
    }
    css += ".sprite .spr {\n\tbackground-image: url(\"".concat(exp.image, "\");\n");
    if (exp.width && exp.height) {
      css += "\tbackground-size: ".concat(exp.width, " ").concat(exp.height, ";\n");
    }
    css += '}\n\n';
    return css;
  };
  var oMod = o;
  Object.keys(o).forEach(function (context) {
    Object.keys(o[context]).forEach(function (experience) {
      oMod[context][experience].context = context;
      oMod[context][experience].experience = experience;
      output += writeRule(oMod[context][experience]);
    });
  });
  return output;
}

/**
 * Write Background Image helper.
 * @returns {string} The generated markup.
 */
function writeBackgroundImage() {
  var breakpoints = [];
  var output = '';
  var writeBreakpoint = function writeBreakpoint(breakpointArgs, breakpoint) {
    var selector = breakpointArgs[0];
    var lowres = [];
    var hires = [];
    var position = [];
    var size = [];
    var mq = '';
    var css = '';
    var _addAttribute = function addAttribute(item, marker, list) {
      if (typeof item === 'undefined' || typeof item[marker] === 'undefined') {
        return list;
      }
      switch (_typeof(item[marker])) {
        case 'string':
          list.push(item[marker]);
          break;
        case 'object':
          {
            var tmp = [];
            Object.keys(item[marker]).forEach(function (key) {
              if (key === 'width' || key === 'height') {
                _addAttribute(item[marker], key, tmp);
              }
            });
            list.push(tmp.join(' '));
            break;
          }
      }
      return list;
    };
    var writeRule = function writeRule(attribute, ruleSelector, values, filter) {
      var value = values.join(', ');
      if (attribute === 'background-image') {
        value = "url(\"".concat(values.join('"), url("'), "\")");
      }
      return values.length > 0 ? "".concat(filter ? "".concat(filter, " ") : '').concat(ruleSelector, " { ").concat(attribute, ": ").concat(value, "; }\n") : '';
    };
    for (var i = 1; i < breakpointArgs.length - 1; i += 1) {
      try {
        var b = breakpointArgs[i][breakpoint];
        lowres = _addAttribute(b, 'lowres', lowres);
        hires = _addAttribute(b, 'hires', hires);
        if (lowres.length > hires.length) {
          hires.push(lowres[lowres.length - 1]);
        }
        size = _addAttribute(b, 'size', size);
        position = _addAttribute(b, 'position', position);
      } catch (err) {
        // Fail silently.
      }
    }
    mq = breakpoint === 'default' ? '' : "html.mq-".concat(breakpoint);
    css += writeRule('background-image', selector, lowres, mq);
    css += writeRule('background-image', selector, hires, "html.mq-hires".concat(mq.replace('html', '')));
    css += writeRule('background-size', selector, size, mq);
    css += writeRule('background-position', selector, position, mq);
    return css;
  };

  // Function should gather all the various breakpoint sizes that apply to all arguments, then
  // run CSS rule-builders for each breakpoint.
  for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
    args[_key] = arguments[_key];
  }
  for (var i = 1; i < args.length - 1; i += 1) {
    Object.keys(args[i]).forEach(function (breakpoint) {
      if (breakpoints.indexOf(breakpoint) === -1) {
        breakpoints.push(breakpoint);
      }
    });
  }

  // Loop through the collective breakpoints to write the rules.
  for (var b = 0; b < breakpoints.length; b += 1) {
    output += writeBreakpoint(args, breakpoints[b]);
  }
  return new Handlebars.SafeString(output);
}

/**
 * Write Dynamic ID helper.
 * @param {string} type - Determines which data will be included in the generated ID.
 * @param {string} base - A prefix for the ID, not including the hyphen which will be added.
 * @param {string} output - If set to "bare", only the ID itself will be returned.
 * @returns {string} The generated `id` attribute markup.
 */
function writeDynamicId(type, base, output) {
  var id = '';
  var content = [];
  var badCharacters = ['&amp;', '[^A-Za-z0-9]'];
  var cleanCharacters = function cleanCharacters(str) {
    var replaceThese = new RegExp(badCharacters.join('|'), 'g');
    var strMod = str.replace(replaceThese, '-');
    strMod = strMod.split('-').filter(function (n) {
      return n !== '';
    }).join('-');
    strMod = strMod.replace(/^-|-$/g, '');
    return strMod;
  };
  var writeAttribute = function writeAttribute(idValue) {
    return " id=\"".concat(idValue, "\"");
  };
  var typeMod = this.id && base ? 'id' : type;
  switch (typeMod) {
    case 'id':
      content.push(this.id);
      break;
    case 'simple':
      if (this.text) {
        content.push(this.text);
      }
      break;
    case 'faq':
      if (this.question) {
        content.push(this.question);
      }
      break;
    case 'guide':
      if (this.heading) {
        content.push(this.heading);
      }
      if (this.subHeading) {
        content.push(this.subHeading);
      }
      break;
    case 'map':
      if (this.meta.type) {
        content.push(this.meta.type);
      }
      if (this.location.street1) {
        content.push(this.location.street1);
      }
      if (this.location.street2) {
        content.push(this.location.street2);
      }
      if (this.location.postal) {
        content.push(this.location.postal);
      }
      break;
  }
  if (content.length > 0) {
    id = content.join('-').toLowerCase();
    id = cleanCharacters(id);
    if (base && base.length > 0 && id.indexOf(base) !== 0) {
      id = "".concat(base, "-").concat(id);
    }
    if (output !== 'bare') {
      id = writeAttribute(id);
    }
  }
  return id;
}

/**
 * Adds a given amount to a given pixel value.
 * @param {string} value - CSS-friendly pixel value to add to.
 * @param {number} change - Number of pixels to be added.
 * @returns {string} The new CSS-friendly pixel value.
 */
function spriteAdd(value, change) {
  if (typeof value !== 'undefined') {
    return "".concat(parseInt(value.replace('px', ''), 10) + change, "px");
  }
  return value;
}

/**
 * Subtracts a given amount from a given pixel value.
 * @param {string} value - CSS-friendly pixel value to subtract from.
 * @param {number} change - Number of pixels to be subtracted.
 * @returns {string} The new CSS-friendly pixel value.
 */
function spriteSubtract(value, change) {
  if (typeof value !== 'undefined') {
    return "".concat(parseInt(value.replace('px', ''), 10) - change, "px");
  }
  return value;
}

/**
 * Increments a number and returns it.
 * @param {*} value - Number to increment.
 * @returns {number} The new incremented value.
 */
function incrementValue(value) {
  if (typeof value !== 'undefined') {
    return parseInt(value, 10) + 1;
  }
  return 0;
}

/**
 * Helper to create a footnote reference.
 * @param {string} target - The ID of the reference.
 * @returns {object} The generated markup wrapped to indicate it should never be escaped.
 */
function footnote(target) {
  var output = "<span class=\"spa-footnote-ref\" data-footnote-ref=\"".concat(target, "\"></span>");
  return new Handlebars.SafeString(output);
}

/**
 * Helper for layout class name logic.
 * @param {string} target - The ID of the reference.
 * @returns {object} The generated markup wrapped to indicate it should never be escaped.
 */
function layoutCollapse(small, medium, large) {
  // String to return.
  var breakpoints = '';

  // Create object to test against.
  var collapseObj = {
    "small": small || "true",
    "medium": medium || "true",
    "large": large || "true"
  };

  // Get first object key/value as a base.
  var collapseObjKeys = Object.keys(collapseObj);
  var firstItemKey = collapseObjKeys[0];
  var firstItemValue = collapseObj[firstItemKey];

  // Delete higher-sized BP's that match lower level.
  collapseObj = deleteObjItem(collapseObj, collapseObjKeys);

  // If everything is the same, default to small-up.
  if (Object.values(collapseObj).every(function (item) {
    return item === firstItemValue;
  })) {
    if (firstItemValue === "false") {
      breakpoints = "".concat(firstItemKey, "-uncollapse");
    } else {
      breakpoints = "".concat(firstItemKey, "-collapse");
    }
  }
  // Add BP specific collapse class.
  else {
    for (var _i = 0, _Object$keys = Object.keys(collapseObj); _i < _Object$keys.length; _i++) {
      var key = _Object$keys[_i];
      if (collapseObj[key] === "false") {
        breakpoints += "".concat(key, "-uncollapse ");
      } else {
        breakpoints += "".concat(key, "-collapse ");
      }
    }
  }
  return breakpoints;
}

/**
 * Helper for layout class name logic.
 * @param {string} target - The ID of the reference.
 * @returns {object} The generated markup wrapped to indicate it should never be escaped.
 */
function layoutColumn(small, medium, large) {
  var breakpoints = '';

  // Create object to test against.
  var columnObj = {
    "small": small || undefined,
    "medium": medium || undefined,
    "large": large || undefined
  };

  // Delete any undefined fields
  Object.keys(columnObj).forEach(function (key) {
    return columnObj[key] === undefined ? delete columnObj[key] : {};
  });

  // Get first object key/value as a base.
  var columnObjKeys = Object.keys(columnObj);

  // Kill check if no data given.
  if (columnObjKeys.length === 0) {
    return '';
  }

  // Delete higher-sized BP's that match lower level.
  columnObj = deleteObjItem(columnObj, columnObjKeys);

  // Get first item to test against
  var firstItemKey = columnObjKeys[0];
  var firstItemValue = columnObj[firstItemKey];

  // If they're all the same, just pass a single class
  if (Object.values(columnObj).every(function (item) {
    return item === firstItemValue;
  })) {
    if (firstItemValue === 12) {
      breakpoints += '';
    } else {
      breakpoints += "".concat(firstItemKey, "-").concat(firstItemValue);
    }
  }
  // Else set state by breakpoint.
  else {
    for (var _i2 = 0, _Object$keys2 = Object.keys(columnObj); _i2 < _Object$keys2.length; _i2++) {
      var key = _Object$keys2[_i2];
      breakpoints += "".concat(key, "-").concat(columnObj[key], " ");
    }
  }
  return breakpoints;
}
export { abbr, ada, debug, debug_print, elementAttributes, footnote, incrementValue, layerContent, layoutCollapse, layoutColumn, link, spriteAdd, spriteRules, spriteSubtract, writeBackgroundImage, writeDynamicId };